import { Counter } from '../_classes/counter';
import { BranchQueue } from '../_classes/branch-queue';
import { ApiCounter } from '../_types/api.types';
import { Monitor } from '../_types/monitor.types';
import { MonitorScreen, MonitorIncludedType, BranchQueueData } from '../_types';

const DEFAULT_FONT_FAMILY = 'Roboto';
const DEFAULT_FONT_COLOR = '#fff';
const DEFAULT_BACKGROUND_COLOR = '#384148';
const DEFAULT_FONT_SIZE = '43';
const DEFAULT_BORDER_COLOR = '#1f2343';

function emptyMonitor(): Monitor {
  return {
    id: '',
    value: '',
    name: '',
    attachments: [],
    counters: [],
    queues: [],
    label: '',
    viewType: '',
    mediaType: '',
    enableCounters: false,
    mediaId: '',
    counterFontFamily: DEFAULT_FONT_FAMILY,
    tokenFontFamily: DEFAULT_FONT_FAMILY,
    counterFontColor: DEFAULT_FONT_COLOR,
    tokenFontColor: DEFAULT_FONT_COLOR,
    backgroundColor: DEFAULT_BACKGROUND_COLOR,
    counterFontSize: DEFAULT_FONT_SIZE,
    tokenFontSize: DEFAULT_FONT_SIZE,
    borderColor: DEFAULT_BORDER_COLOR,
    itemType: '',
    enableGroupCounterByName: false,
    playTokenFetchSound: false,
    playSoundOnAllBranchQueues: false,
    playSoundBehaviourType: 'people_in_queue_from_0_to_1',
  };
}

/** Parse raw monitor-screen API data into a `Monitor`. No args → empty default. */
export function parseMonitor(data?: MonitorScreen, included: MonitorIncludedType = []): Monitor {
  const monitor = emptyMonitor();
  if (!data) return monitor;

  const { attributes, id } = data;

  monitor.id = id;
  monitor.name = attributes.name;
  monitor.value = monitor.id;
  monitor.label = monitor.name;
  monitor.itemType = attributes.itemType;
  monitor.attachments = attributes.media;
  monitor.viewType = attributes.view;
  monitor.mediaType = attributes?.mediaType?.toLowerCase() ?? 'image';
  monitor.enableCounters = attributes.enableCounters;
  monitor.enableGroupCounterByName = attributes?.enableGroupCounterByName ?? false;
  monitor.mediaId = attributes.urlId;
  monitor.playTokenFetchSound = attributes?.playTokenFetchSound ?? false;
  monitor.playSoundOnAllBranchQueues = attributes?.playSoundOnAllBranchQueues ?? false;
  monitor.playSoundBehaviourType = attributes?.playSoundBehaviourType ?? 'people_in_queue_from_0_to_1';

  setFontValues(monitor, data);
  setMonitorItems(monitor, included);

  return monitor;
}

function setFontValues(monitor: Monitor, data: MonitorScreen) {
  const { attributes } = data;
  monitor.tokenFontFamily = attributes.tokenFontFamily || DEFAULT_FONT_FAMILY;
  monitor.counterFontFamily = attributes.counterFontFamily || DEFAULT_FONT_FAMILY;
  monitor.counterFontColor = attributes.counterFontColor || DEFAULT_FONT_COLOR;
  monitor.tokenFontColor = attributes.tokenFontColor || DEFAULT_FONT_COLOR;
  monitor.backgroundColor = attributes.backgroundColor || DEFAULT_BACKGROUND_COLOR;
  monitor.borderColor = attributes.borderColor || DEFAULT_BORDER_COLOR;
  monitor.counterFontSize = attributes.counterFontSize || DEFAULT_FONT_SIZE;
  monitor.tokenFontSize = attributes.tokenFontSize || DEFAULT_FONT_SIZE;
}

function setMonitorItems(monitor: Monitor, included: MonitorIncludedType) {
  if (!included) return;

  if (monitor.itemType === 'counter') {
    monitor.counters = buildCounters(included.filter((it) => it.type === 'counters') as ApiCounter[]);
  } else if (monitor.itemType === 'branch_queue') {
    monitor.queues = buildQueues(included.filter((it) => it.type === 'branchQueues') as BranchQueueData[], monitor.id);
  }
}

function buildCounters(data: ApiCounter[]): Counter[] {
  return (data || []).map((it) => {
    const counter = new Counter();
    counter.setCounterData(it);
    return counter;
  });
}

function buildQueues(data: BranchQueueData[], monitorId: string): BranchQueue[] {
  return (data || []).map((it) => {
    const queue = new BranchQueue();
    const monitorQueue = it.attributes?.monitorQueues?.find((x) => `${x.monitorScreenId}` == monitorId);
    queue.setBranchQueueData(it, monitorQueue ? monitorQueue.displayName : '');
    return queue;
  });
}
